import { useMutation, useQueryClient } from '@tanstack/react-query';
import partition from 'lodash/partition';
import pluralize from 'pluralize';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';

import { apiClient } from '@cast/infrastructure-api';

import { getClustersQueryKey } from '@cast/shared-cluster-context-data-access';

export const useRemoveNotFoundClustersMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clusterIds: string[]) =>
      Promise.allSettled(
        clusterIds.map((clusterId) =>
          apiClient.clusters.deleteCluster({ clusterId })
        )
      ),
    onSuccess: (results) => {
      const [fulfilled, rejected] = partition(
        results,
        (result) => result.status === 'fulfilled'
      );

      if (fulfilled.length) {
        onSuccess?.();
        queryClient.invalidateQueries({
          queryKey: getClustersQueryKey({}),
        });
      }

      if (fulfilled.length === results.length) {
        notify.success(
          `${fulfilled.length} ${pluralize(
            'cluster',
            fulfilled.length
          )} removed`
        );
      } else if (rejected.length === results.length) {
        notify.error(
          `Failed to remove ${rejected.length} ${pluralize(
            'cluster',
            rejected.length
          )}: ${parseApiError(
            rejected[0].status === 'rejected' ? rejected[0].reason : undefined
          )}`
        );
      } else {
        notify.warning(
          `${fulfilled.length} out of ${results.length} ${pluralize(
            'cluster',
            results.length
          )} removed`
        );
      }
    },
    onError: (error) => {
      notify.error(`Failed to remove clusters: ${parseApiError(error)}`);
    },
  });
};
